import { Radio, Collapse } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaQuestionCircle } from '@react-icons/all-files/fa/FaQuestionCircle';
import { FaMagic } from '@react-icons/all-files/fa/FaMagic';

import './index.scss';
import Avatar from 'react-avatar';
import {
  addCommas,
  getPriceText,
} from '@zorba-shared/core/Services/textService';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import {
  BUYER_REJECTION_REASON_OPTIONS,
  BUYER_REJECTION_REASON_OTHER_SOURCE,
  DAYS_TO_CLOSE_OPTIONS,
  getLatestAskingPriceBySeller,
  INSPECTION_DAYS_OPTIONS,
  SELLER_REJECTION_FRIENDLY_TITLES,
  BUYER_REJECTION_REASON_TOO_MANY_YELLOW_FLAGS,
  BUYER_YELLOW_FLAGS_OPTIONS,
  DEFAULT_FEE_AMOUNT,
} from '@zorba-shared/core/Constants/offer';
import { getFriendlyTime } from '../../../../../../services/textService';
import ZorbaButton from '../../../../../../components/forms/ZorbaButton';
import ZorbaInput from '../../../../../../components/forms/ZorbaInput';
import {
  BUTTON_VARIANT_DARK,
  BUTTON_VARIANT_LIGHT,
} from '../../../../../../components/forms/ZorbaButton/constants';
import {
  updateOffer,
  fetchPendingActions,
  setSelectedOffer,
  setSelectedOfferForContact,
} from '../../../../../../reducers/Offer/actions';
import { scrollActivityLogToBottom } from '../../../../../../services/offerService';
import { ZorbaSelect } from '../../../../../../components/forms';
import { reportEvent } from '../../../../../../services/analyticsService';
import { HOMEWARD_ID, OFFER_PARAMS, POSSIBLE_ACTIONS } from './constants';
import { setModalState } from '../../../../../../reducers/Modal/actions';
import { MODAL_CONTACT_SPECIALIST } from '../../../../../../reducers/Modal/types';
import { postRequest } from '../../../../../../services/requestService';

const getDiffBetweenNewOfferAndOldOffer = (events, newOffer) => {
  if (!events || !newOffer) return {};

  const newParams = _.pick(newOffer, OFFER_PARAMS);
  const lastOfferEvent = _.last(
    events.filter((event) => event.kind === 'offer'),
  );
  if (!lastOfferEvent) {
    return { ...newOffer };
  }
  const diffObject = {};
  for (const param of OFFER_PARAMS) {
    if (newParams[param] !== lastOfferEvent[param]) {
      diffObject[param] = newParams[param];
    }
  }

  return diffObject;
};

export default ({ type }) => {
  const { selectedOffer, isFetchingOffers } = useSelector(
    ({ OfferReducer }) => OfferReducer,
  );
  const { userDetails, organization, marketplacePartnerData } = useSelector(
    ({ AuthReducer }) => AuthReducer,
  );
  console.log(marketplacePartnerData);
  const isHomewardUser = organization?._id === HOMEWARD_ID;
  const doesIncludeFeeInOffer = organization.buyerConfig?.doesIncludeFeeInOffer;
  const isCustomMarketplace = Boolean(
    userDetails?.defaultOrganization?.marketplacePartner,
  );

  const lastOffer = _.last(
    selectedOffer.events.filter((event) => event.kind === 'offer'),
  );
  const offerRequest = _.last(
    selectedOffer.events.filter((event) => event.kind === 'offer_request'),
  );
  const isCounterOfferLastEvent =
    _.last(selectedOffer.events)?.kind === 'seller_update_asking_price' &&
    selectedOffer.events.some((event) => event.kind === 'offer');
  const isInTheFinalOfferMode =
    _.last(selectedOffer.events)?.kind === 'firm_on_offer' ||
    (_.last(selectedOffer.events)?.kind === 'offer' &&
      _.last(selectedOffer.events)?.isFinalOffer);

  const [activeCollapseKeys, setActiveCollapseKeys] = useState(
    isCounterOfferLastEvent ? [] : ['offer_terms'],
  );

  const dispatch = useDispatch();
  const [action, setAction] = useState(
    isCounterOfferLastEvent ? POSSIBLE_ACTIONS.OFFER : '',
  );

  const { control, reset, watch, getValues } = useForm({
    defaultValues: {},
  });

  const getTitleCompanyName = () => {
    if (
      marketplacePartnerData?.kind === 'title' &&
      marketplacePartnerData?.entityName
    ) {
      return marketplacePartnerData.entityName;
    }

    if (
      marketplacePartnerData?.kind === 'lender' &&
      marketplacePartnerData?.name === 'All Ways Capital'
    ) {
      return 'BCHH TITLE';
    }

    if (
      marketplacePartnerData?.kind === 'lender' &&
      marketplacePartnerData?.name === 'Yorktown Capital'
    ) {
      return 'TitleVest';
    }

    if (selectedOffer?.matchedBuyBox?.titleCompanyName) {
      return selectedOffer.matchedBuyBox.titleCompanyName;
    }

    return userDetails.defaultOrganization?.titleCompanyName || '';
  };

  const getLenderCompanyName = () => {
    if (
      marketplacePartnerData?.kind === 'lender' &&
      marketplacePartnerData?.entityName
    ) {
      return marketplacePartnerData.entityName;
    }

    return '';
  };

  useEffect(() => {
    if (selectedOffer) {
      reset({
        daysToClose: lastOffer?.daysToClose || offerRequest.daysToClose,
        inspectionDays:
          lastOffer?.inspectionDays || offerRequest.inspectionDays,
        inspectionMode:
          lastOffer?.inspectionMode || offerRequest.inspectionMode,
        emd: lastOffer?.emd || offerRequest.emd,
        titleCompanyName: lastOffer?.titleCompanyName || getTitleCompanyName(),
        financingType:
          lastOffer?.financingType || marketplacePartnerData?.kind === 'lender'
            ? 'loan'
            : 'cash',
        lenderName: lastOffer?.lenderName || getLenderCompanyName(),
        estimatedProceeds: lastOffer?.estimatedProceeds,
      });
    }
  }, [selectedOffer]);

  const isChanged =
    Object.keys(
      getDiffBetweenNewOfferAndOldOffer(selectedOffer.events, getValues()),
    ).length > 0;

  const emdValue = watch('emd');
  const inspectionDaysValue = watch('inspectionDays');
  const titleCompanyNameValue = watch('titleCompanyName');
  const daysToCloseValue = watch('daysToClose');
  const amountValue = watch('amount');
  const rejectionReason = watch('rejectionReason');
  const inspectionMode = watch('inspectionMode');
  const yellowFlags = watch('yellowFlagReasons');
  const financingType = watch('financingType');
  const lenderName = watch('lenderName');

  const onSubmit = () => {
    const formValues = getValues();
    reportEvent('offer_view', `action_submit_${action}`);
    dispatch(
      updateOffer(
        {
          ...formValues,
          amount: doesIncludeFeeInOffer
            ? formValues.amount -
              (selectedOffer.feeAmount || DEFAULT_FEE_AMOUNT)
            : formValues.amount,
          rawAmount: formValues.amount,
          action,
          dealSubmissionMatchId: selectedOffer._id,
          dealSubmissionId:
            selectedOffer.dealSubmission?._id || selectedOffer.dealSubmission,
          inspectionDays:
            inspectionMode === 'pre_inspection'
              ? null
              : formValues.inspectionDays,
          transactionType: userDetails?.defaultOrganization?.buyerConfig
            ?.requireDoubleClose
            ? 'double_close'
            : 'assignment',
          isFinalOffer:
            action === POSSIBLE_ACTIONS.OFFER && isCounterOfferLastEvent,
        },
        () => {
          setAction();
          scrollActivityLogToBottom();
        },
      ),
    );
    dispatch(fetchPendingActions());
    reset({
      message: '',
      amount: null,
    });
  };

  if (!selectedOffer?.dealSubmission) return null;

  if (!selectedOffer || Object.keys(selectedOffer).length === 0) return null;
  const reversedEvents = selectedOffer.events || [];

  const isValid =
    (action === POSSIBLE_ACTIONS.OFFER &&
      amountValue &&
      daysToCloseValue &&
      titleCompanyNameValue &&
      emdValue &&
      inspectionMode &&
      (financingType !== 'loan' || lenderName) &&
      (inspectionMode === 'pre_inspection' || inspectionDaysValue) &&
      isChanged) ||
    (action === POSSIBLE_ACTIONS.REJECTED_BY_BUYER &&
      rejectionReason &&
      rejectionReason !== BUYER_REJECTION_REASON_TOO_MANY_YELLOW_FLAGS) ||
    (action === POSSIBLE_ACTIONS.REJECTED_BY_BUYER &&
      rejectionReason &&
      rejectionReason === BUYER_REJECTION_REASON_TOO_MANY_YELLOW_FLAGS &&
      yellowFlags) ||
    action === POSSIBLE_ACTIONS.FIRM_ON_OFFER;

  const possibleActions = [
    ...(!isInTheFinalOfferMode
      ? [
          {
            label: isCounterOfferLastEvent
              ? 'Update final bid'
              : 'Send/Update Offer',
            value: POSSIBLE_ACTIONS.OFFER,
          },
        ]
      : []),
    ...(isCounterOfferLastEvent
      ? [{ label: 'Firm on offer', value: POSSIBLE_ACTIONS.FIRM_ON_OFFER }]
      : []),
    { label: 'Reject this deal', value: POSSIBLE_ACTIONS.REJECTED_BY_BUYER },
  ];

  const getActionDescription = () => {
    if (action === POSSIBLE_ACTIONS.OFFER && isCounterOfferLastEvent)
      return `You can set your final bid in response to the seller’s counter offer`;
    if (action === POSSIBLE_ACTIONS.OFFER)
      return 'You are submitting an offer directly to the seller, pay attention to the terms they requested.';
    if (action === POSSIBLE_ACTIONS.REJECTED_BY_BUYER)
      return 'You are letting the seller know that your offer for this property is no longer on the table.';
    if (action === POSSIBLE_ACTIONS.FIRM_ON_OFFER)
      return 'You want to maintain the original offer without making any changes (highest and best).';

    return '';
  };

  const getSubmitButtonText = () => {
    if (action === POSSIBLE_ACTIONS.OFFER && isCounterOfferLastEvent)
      return `Submit`;
    if (action === POSSIBLE_ACTIONS.OFFER) return 'Send/Update Offer';
    if (action === POSSIBLE_ACTIONS.REJECTED_BY_BUYER)
      return 'Reject this deal';
    if (action === POSSIBLE_ACTIONS.FIRM_ON_OFFER) return 'Submit';

    return '';
  };

  const lastAskingPrice = getLatestAskingPriceBySeller(
    selectedOffer.dealSubmission,
  );

  const handleOpenAdvisorModal = () => {
    dispatch(setSelectedOfferForContact(selectedOffer));
    dispatch(setSelectedOffer());
    dispatch(setModalState(MODAL_CONTACT_SPECIALIST, true));
  };

  return (
    <div className="activity-tab">
      <div className="feed">
        <div className="activity-feed-container">
          <h3>Activity feed</h3>
        </div>
        <div className="inner" id="activity-log-inner">
          {reversedEvents.map((eventObj, idx) => {
            const TEXT_OBJ = {
              offer_request: {
                name: 'Zorba',
                desc: () => `requested an offer`,
              },
              offer: {
                name: eventObj.user?.name,
                desc: (eventObj) => {
                  const { updatedParams } = eventObj;
                  if (!updatedParams) return '';
                  let msg = 'updated the offer to ';
                  const MAP = {
                    ...(doesIncludeFeeInOffer
                      ? {
                          rawAmount: (val) =>
                            `<b>${getPriceText(val)}</b> amount`,
                        }
                      : {
                          amount: (val) => `<b>${getPriceText(val)}</b> amount`,
                        }),
                    emd: (val) => `<b>${getPriceText(val)}</b> EMD`,
                    inspectionMode: (val) =>
                      `<b>${
                        val === 'pre_inspection'
                          ? 'Pre-Inspection'
                          : 'Contingency'
                      }</b> mode`,
                    inspectionDays: (val) =>
                      val && `<b>${val}</b> inspection days`,
                    daysToClose: (val) => `<b>${val}</b> days to close`,
                    titleCompanyName: (val) =>
                      `set the title to be <b>${val}</b>`,
                  };

                  msg += Object.keys(updatedParams)
                    .map((param) => {
                      const func = MAP[param];
                      if (!func) return '';
                      return func(updatedParams[param]);
                    })
                    .filter(Boolean)
                    .join(', ');

                  return msg;
                },
              },
              rejected_by_buyer: {
                name: eventObj.user?.name,
                desc: (eventObj) => 'rejected this deal',
              },
              rejected_by_seller: {
                name: 'Seller',
                desc: (eventObj) =>
                  `has rejected your offer. Reason: ${
                    SELLER_REJECTION_FRIENDLY_TITLES[eventObj.message]
                  }`,
              },
              seller_update_asking_price: {
                name: 'Seller',
                desc: (eventObj) => {
                  const { updatedParams } = eventObj;
                  if (!updatedParams) return '';
                  let msg = 'updated an offer to ';
                  const MAP = {
                    feeAmount: (val) =>
                      `<b>${getPriceText(
                        doesIncludeFeeInOffer
                          ? eventObj.amount + val
                          : eventObj.amount,
                      )}</b>`,
                    updatedAskingPrice: () =>
                      `<b>${getPriceText(
                        doesIncludeFeeInOffer
                          ? eventObj.amount +
                              (selectedOffer.feeAmount || DEFAULT_FEE_AMOUNT)
                          : eventObj.amount,
                      )}</b>`,
                    emd: (val) => `<b>${getPriceText(val)}</b> EMD`,
                    inspectionDays: (val) => `<b>${val}</b> inspection days`,
                    inspectionMode: (val) =>
                      `<b>${
                        val === 'pre_inspection'
                          ? 'Pre-Inspection'
                          : 'Contingency'
                      }</b> mode`,
                    daysToClose: (val) => `<b>${val}</b> days to close`,
                    titleCompanyName: (val) =>
                      `set the title to be <b>${val}</b>`,
                  };

                  msg += Object.keys(updatedParams)
                    .filter(
                      (param) =>
                        !(
                          param === 'feeAmount' &&
                          'updatedAskingPrice' in updatedParams
                        ),
                    )
                    .map((param) => {
                      const func = MAP[param];
                      if (!func) return '';
                      return func(updatedParams[param]);
                    })
                    .filter(Boolean)
                    .join(', ');

                  return msg;
                },
              },
              firm_on_offer: {
                name: eventObj.user?.name,
                desc: () =>
                  'updated that this is the highest and best offer for this deal.',
              },
            };
            const getAvatarPath = () => {
              if (eventObj.kind === 'offer_request') {
                return 'img/brand/zorba-icon.png';
              }
              if (eventObj.kind.includes('seller')) {
                return '/img/user/avatar.png';
              }
              return eventObj.user?.picture;
            };

            return (
              <div className="activity-item" key={eventObj._id}>
                <div className="left">
                  <Avatar
                    name={eventObj.user?.name}
                    src={getAvatarPath()}
                    round
                    size={40}
                  />
                </div>
                <div className="right">
                  <div className="text">
                    <b>{TEXT_OBJ[eventObj.kind].name}</b>{' '}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: TEXT_OBJ[eventObj.kind].desc(eventObj),
                      }}
                    />
                  </div>
                  <div className="date">
                    {getFriendlyTime(eventObj.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="right">
        <Collapse
          onChange={(e) => setActiveCollapseKeys(e)}
          defaultActiveKey={activeCollapseKeys}
          activeKey={activeCollapseKeys}
          items={[
            {
              key: 'offer_terms',
              label: 'Offer terms',

              children: (
                <div className="offer-details">
                  <div className="stats">
                    {selectedOffer.finalizedOffer && (
                      <div className="stat-item bold">
                        <div className="title">Current offer:</div>
                        <div className="value">
                          {selectedOffer.finalizedOffer
                            ? `${getPriceText(
                                doesIncludeFeeInOffer
                                  ? lastOffer?.rawAmount
                                  : selectedOffer.finalizedOffer,
                              )}`
                            : '-'}
                        </div>
                      </div>
                    )}
                    {lastOffer?.estimatedProceeds && (
                      <div className="stat-item">
                        <div className="title">
                          Estimated proceeds after second sale:
                        </div>
                        <div className="value">
                          {getPriceText(lastOffer.estimatedProceeds)}
                        </div>
                      </div>
                    )}
                    <div className="stat-item">
                      <div className="title">
                        {doesIncludeFeeInOffer && !isCustomMarketplace
                          ? `Seller's asking price (including our fee):`
                          : `Seller's asking price:`}
                      </div>
                      <div className="value">
                        {doesIncludeFeeInOffer
                          ? getPriceText(
                              lastAskingPrice +
                                (selectedOffer.feeAmount || DEFAULT_FEE_AMOUNT),
                            )
                          : getPriceText(lastAskingPrice)}
                      </div>
                    </div>
                    {!doesIncludeFeeInOffer && (
                      <div className="stat-item">
                        <div className="title">Zorba’s fee:</div>
                        <div className="value">
                          {getPriceText(
                            selectedOffer.feeAmount || DEFAULT_FEE_AMOUNT,
                          )}
                        </div>
                      </div>
                    )}
                    <div className="stat-item">
                      <div className="title">Inspection period:</div>
                      <div className="value">
                        {offerRequest?.inspectionMode === 'pre_inspection'
                          ? 'Pre-Inspection'
                          : `${selectedOffer.offerParams.inspectionDays} business days`}
                      </div>
                    </div>
                    {(lastOffer?.daysToClose ||
                      selectedOffer.offerParams.daysToClose) && (
                      <div className="stat-item">
                        <div className="title">Days to close:</div>
                        <div className="value">{`${
                          lastOffer?.daysToClose ||
                          selectedOffer.offerParams.daysToClose
                        } days`}</div>
                      </div>
                    )}
                    <div className="stat-item">
                      <div className="title">EMD:</div>
                      <div className="value">
                        {getPriceText(selectedOffer.offerParams.emd)}
                      </div>
                    </div>
                    {selectedOffer.offerParams.titleCompanyName && (
                      <div className="stat-item">
                        <div className="title">Title:</div>
                        <div className="value">
                          {selectedOffer.offerParams.titleCompanyName}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />

        {!['rejected_by_buyer', 'rejected_by_seller'].includes(
          selectedOffer.status,
        ) &&
          !type && (
            <div className="offer-actions">
              <Radio.Group
                buttonStyle="solid"
                optionType="button"
                options={possibleActions}
                onChange={(e) => {
                  reportEvent('offer_view', `action_select_${e.target.value}`);
                  setActiveCollapseKeys([]);
                  setAction(e.target.value);
                }}
                value={action}
                className="full-width-radio-group"
              />
              <span className="action-description">
                {getActionDescription()}
              </span>

              {/* UPDATE OFFER FORM */}
              {action === 'offer' && (
                <div className="update-offer-form">
                  <div className="row">
                    <Controller
                      name="amount"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <ZorbaInput
                          type="number"
                          prefix="$"
                          symbol=""
                          label={
                            doesIncludeFeeInOffer
                              ? 'All-in offer'
                              : 'Offer to seller'
                          }
                          field={field}
                        />
                      )}
                    />
                  </div>
                  {isHomewardUser && (
                    <div className="row">
                      <Controller
                        name="estimatedProceeds"
                        control={control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <ZorbaInput
                            type="number"
                            prefix="$"
                            symbol=""
                            label="Estimated proceeds after second sale"
                            field={field}
                          />
                        )}
                      />
                    </div>
                  )}
                  {isCustomMarketplace && (
                    <div className="row two-items">
                      <Controller
                        name="financingType"
                        control={control}
                        render={({ field }) => (
                          <ZorbaSelect
                            label="Financing Type"
                            options={[
                              { text: 'Cash', value: 'cash' },
                              { text: 'Loan', value: 'loan' },
                            ]}
                            field={field}
                          />
                        )}
                      />
                      {financingType === 'loan' && (
                        <Controller
                          name="lenderName"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <ZorbaInput label="Lender name" field={field} />
                          )}
                        />
                      )}
                    </div>
                  )}
                  <div className="row two-items">
                    <Controller
                      name="inspectionMode"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <ZorbaSelect
                          label="Inspection Mode"
                          options={[
                            { text: 'Pre inspection', value: 'pre_inspection' },
                            { text: 'Contingency', value: 'contingency' },
                          ]}
                          field={field}
                        />
                      )}
                    />
                    {inspectionMode === 'contingency' && (
                      <Controller
                        name="inspectionDays"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <ZorbaSelect
                            label="Inspection period"
                            options={INSPECTION_DAYS_OPTIONS}
                            field={field}
                          />
                        )}
                      />
                    )}
                  </div>
                  <div className="row two-items">
                    <Controller
                      name="daysToClose"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <ZorbaSelect
                          label="Days to close"
                          options={DAYS_TO_CLOSE_OPTIONS}
                          field={field}
                        />
                      )}
                    />
                    <Controller
                      name="emd"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <ZorbaInput
                          type="number"
                          prefix="$"
                          symbol=""
                          label="EMD"
                          field={field}
                          placeholder={addCommas(
                            selectedOffer.offerParams?.emd,
                          )}
                        />
                      )}
                    />
                  </div>
                  <div className="row two-items">
                    <Controller
                      name="titleCompanyName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <ZorbaInput
                          label="Title company name"
                          field={field}
                          placeholder={
                            marketplacePartnerData?.entityName
                              ? marketplacePartnerData.entityName
                              : 'BCHH Title, LLC'
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              {/* REJECT OFFER FORM */}
              {action === 'rejected_by_buyer' && (
                <>
                  <Controller
                    name="rejectionReason"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <ZorbaSelect
                        label="Rejection reason*"
                        className="rejection-reason-select"
                        field={field}
                        options={BUYER_REJECTION_REASON_OPTIONS}
                      />
                    )}
                  />
                  {rejectionReason === BUYER_REJECTION_REASON_OTHER_SOURCE && (
                    <>
                      <Controller
                        name="wholesalerName"
                        control={control}
                        render={({ field }) => (
                          <ZorbaInput
                            placeholder="Wholesaler name"
                            field={field}
                          />
                        )}
                      />
                      <div className="note">
                        We’ll make sure to exclude this wholesaler from your
                        account.
                      </div>
                    </>
                  )}
                  {rejectionReason ===
                    BUYER_REJECTION_REASON_TOO_MANY_YELLOW_FLAGS && (
                    <>
                      <Controller
                        name="yellowFlagReasons"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <ZorbaSelect
                            label="Yellow flags*"
                            mode="multiple"
                            className="rejection-reason-select"
                            field={field}
                            tooltip="You may select multiple yellow flags"
                            options={BUYER_YELLOW_FLAGS_OPTIONS}
                          />
                        )}
                      />
                    </>
                  )}
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <ZorbaInput
                        placeholder="Please enter additional message ..."
                        field={field}
                      />
                    )}
                  />

                  <div className="note">
                    {`We'd like to make sure to improve the quality of the properties we send you`}
                  </div>
                </>
              )}

              {/* {isCustomMarketplace &&
                marketplacePartnerData?.name !== 'All Ways Capital' && (
                  <ZorbaButton
                    variant={BUTTON_VARIANT_LIGHT}
                    icon={<FaMagic />}
                    style={{
                      height: '40px',
                      marginBottom: '25px',
                      marginTop: 20,
                    }}
                    onClick={async () => {
                      reportEvent('offer_view', 'action_ai_underwrite');
                      window.open('https://zorba-five.vercel.app/');
                      await postRequest('/offer/ai-underwrite', {
                        dealSubmissionId: selectedOffer.dealSubmission?._id,
                      });
                    }}
                  >
                    Have AI underwrite & draft the offer
                  </ZorbaButton>
                )} */}
              <ZorbaButton
                variant={BUTTON_VARIANT_LIGHT}
                icon={<FaQuestionCircle />}
                style={{ height: '40px' }}
                onClick={handleOpenAdvisorModal}
              >
                Talk to a marketplace specialist
              </ZorbaButton>

              {['offer', 'rejected_by_buyer', 'firm_on_offer'].includes(
                action,
              ) && (
                <ZorbaButton
                  loading={isFetchingOffers}
                  disabled={!isValid}
                  variant={BUTTON_VARIANT_DARK}
                  className="submit-btn"
                  onClick={onSubmit}
                >
                  {getSubmitButtonText()}
                </ZorbaButton>
              )}
            </div>
          )}
      </div>
    </div>
  );
};
